import type { ErrorEvent, EventHint } from '@sentry/core'

// ─── Noise patterns ───────────────────────────────────────────────────

const CHUNK_LOAD_PATTERNS: RegExp[] = [
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
]

const EXTENSION_ORIGINS: string[] = ['chrome-extension://', 'moz-extension://']

// Dev-only frames (React Fast Refresh, Vite internals) — never emitted by a production
// build. Defense-in-depth behind the sentry-bootstrap dev gate.
const DEV_TOOLING_FRAME_PATTERNS: RegExp[] = [
  /@react-refresh/,
  /\/@vite\//,
  /\/\.vite\/deps\//,
  /\/@fs\//,
]

// Standalone bare network error messages that produce no in-app stack
const BARE_NETWORK_ERROR_MESSAGES: string[] = ['NetworkError', 'Failed to fetch', 'Load failed']

// ─── PII-sensitive header names ──────────────────────────────────────

const SENSITIVE_HEADER_NAMES: RegExp = /^(authorization|x-impersonate-token|cookie|set-cookie|x-api-key)$/i

// Matches token/password/secret/authorization/jwt in any key (case-insensitive)
const SENSITIVE_EXTRA_KEY_PATTERN: RegExp = /token|password|secret|authorization|jwt/i

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isChunkLoadError(event: ErrorEvent): boolean {
  const value = event.exception?.values?.[0]?.value ?? ''
  const type = event.exception?.values?.[0]?.type ?? ''
  return (
    CHUNK_LOAD_PATTERNS.some((p) => p.test(value)) ||
    type === 'ChunkLoadError'
  )
}

function isBrowserExtensionError(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
  if (frames.length === 0) return false
  // Drop if ALL non-empty frames originate from extension URLs
  const nonEmptyFrames = frames.filter((f) => !!f.filename)
  if (nonEmptyFrames.length === 0) return false
  return nonEmptyFrames.every((f) =>
    EXTENSION_ORIGINS.some((origin) => f.filename?.startsWith(origin)),
  )
}

function isDevToolingNoise(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
  // Match only dev-tooling frames (safe — absent from prod bundles, even localhost-served
  // builds like Electron). The bootstrap dev gate already blocks genuine `pnpm dev` events.
  return frames.some((f) =>
    DEV_TOOLING_FRAME_PATTERNS.some((p) => p.test(f.filename ?? '')),
  )
}

function isBareNetworkError(event: ErrorEvent): boolean {
  const value = event.exception?.values?.[0]?.value ?? ''
  if (!BARE_NETWORK_ERROR_MESSAGES.includes(value)) return false
  // Only drop when there are no in-app frames — a real error containing these
  // substrings but with a proper stack trace must pass through.
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
  const inAppFrames = frames.filter(
    (f) => !!f.filename && !EXTENSION_ORIGINS.some((origin) => f.filename?.startsWith(origin)),
  )
  return inAppFrames.length === 0
}

function scrubHeaders(
  headers: Record<string, string>,
): Record<string, string> {
  const scrubbed: Record<string, string> = {}
  for (const [key, value] of Object.entries(headers)) {
    scrubbed[key] = SENSITIVE_HEADER_NAMES.test(key) ? '[redacted]' : value
  }
  return scrubbed
}

function scrubExtra(
  extra: Record<string, unknown>,
): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(extra)) {
    scrubbed[key] = SENSITIVE_EXTRA_KEY_PATTERN.test(key) ? '[redacted]' : value
  }
  return scrubbed
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Sentry beforeSend hook for the client (@sentry/react). The Cloudflare
 * Worker uses @sentry/cloudflare separately and does not call this.
 *
 * Drop known-noise events and scrub PII from headers/extra.
 * Always returns null (drop) or a clean copy of the event (send).
 */
export function beforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  // ── Noise filter: drop unactionable errors ──────────────────────────────────
  if (isChunkLoadError(event)) return null
  if (isBrowserExtensionError(event)) return null
  if (isDevToolingNoise(event)) return null
  if (isBareNetworkError(event)) return null

  // ── PII scrub: redact sensitive fields before transmission ──────────────────
  const cleaned: ErrorEvent = { ...event }

  if (cleaned.request?.headers && typeof cleaned.request.headers === 'object') {
    cleaned.request = {
      ...cleaned.request,
      headers: scrubHeaders(cleaned.request.headers as Record<string, string>),
    }
  }

  // Scrub request.cookies — Sentry's RequestEventData has a cookies field separate
  // from request.headers. Redact all values since cookie names are unpredictable.
  // Handles both the object form ({ name: value }) and a raw cookie string.
  if (cleaned.request?.cookies) {
    if (typeof cleaned.request.cookies === 'string') {
      cleaned.request = { ...cleaned.request, cookies: '[redacted]' }
    } else if (typeof cleaned.request.cookies === 'object') {
      const rawCookies = cleaned.request.cookies as Record<string, string>
      const scrubbedCookies: Record<string, string> = {}
      for (const key of Object.keys(rawCookies)) {
        scrubbedCookies[key] = '[redacted]'
      }
      cleaned.request = { ...cleaned.request, cookies: scrubbedCookies }
    }
  }

  if (cleaned.extra && typeof cleaned.extra === 'object') {
    cleaned.extra = scrubExtra(cleaned.extra as Record<string, unknown>)
  }

  return cleaned
}
